import { DEFAULT_SIGNED_DOCUMENT_DISPLAY_NAME } from '@common/constants';
import type { ClientDocumentEntry } from '@common/types/prospects';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import {
    Alert,
    IconButton,
    Link,
    Menu,
    MenuItem,
    Stack,
    Typography,
    useTheme,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import DownloadButton from '@/components/Buttons/DownloadButton';
import DataTable, { type Column } from '@/components/DataTable';
import { getProspectClientDocuments } from '@/services/ProspectService';
import useIdentityStore from '@/stores/useIdentityStore';
import ProspectDetailsErrorPage from './ProspectDetailsErrorPage';
import VoidRetainerDialog from './VoidRetainerDialog';

interface ClientDocumentRow {
    fileId: string;
    contactId: string | null;
    downloadLink: string;
    documentName: string;
    typeOfDocument: string;
    isSignedLegalDocument: boolean;
    fileType: string;
    relatedClient: string;
    dateOfUpload: string;
}

type ButtonState = 'default' | 'success' | 'loading';

const DOCX_MIME = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';

const DRAFT_INTAKE_DOCUMENTS_DISCLAIMER =
    'These documents are from an in-progress intake. They may not reflect the final state of the client’s submission.';

function createFullName(
    firstName: string | null | undefined,
    middleName: string | null | undefined,
    lastName: string | null | undefined,
): string {
    return [firstName, middleName, lastName]
        .filter((name) => name && name.trim().length > 0)
        .join(' ');
}

function transformClientDocuments(entries: ClientDocumentEntry[]): ClientDocumentRow[] {
    return entries.map((item) => ({
        fileId: item.fileDetails.fileId,
        contactId: item.contactId,
        downloadLink: `${import.meta.env.VITE_APP_API_URL}/api/v1/files/${item.fileDetails.fileId}`,
        documentName: item.fileDetails.fileName,
        typeOfDocument: item.fileDetails.typeOfId
            ? `${item.fileDetails.typeOfDocument}: ${item.fileDetails.typeOfId}`
            : item.fileDetails.typeOfDocument !== 'undefined'
              ? (item.fileDetails.typeOfDocument ?? '')
              : '',
        isSignedLegalDocument: item.fileDetails.typeOfDocument === 'Retainer Agreement',
        fileType:
            item.fileDetails.fileType === DOCX_MIME ? 'word document' : item.fileDetails.fileType,
        relatedClient: createFullName(
            item.userContactDetail?.firstName,
            item.userContactDetail?.middleName,
            item.userContactDetail?.lastName,
        ),
        // When the file was added to this prospect. A template's attachments are shared files,
        // so the file's own date is when it was uploaded to the template
        dateOfUpload: new Date(item.createdAt).toLocaleDateString(),
    }));
}

interface Props {
    prospectId?: string;
    selectedVersionId: number | null;
    isDraftPreview?: boolean;
}

export default function ClientDocuments({
    prospectId,
    selectedVersionId,
    isDraftPreview = false,
}: Props) {
    const theme = useTheme();
    const isSuperAdmin = useIdentityStore((state) => state.isSuperAdmin);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [documentButtonState, setDocumentButtonState] = useState<Record<string, ButtonState>>({});
    const [actionsMenu, setActionsMenu] = useState<{
        anchorEl: HTMLElement;
        row: ClientDocumentRow;
    } | null>(null);
    const [voidingRow, setVoidingRow] = useState<ClientDocumentRow | null>(null);

    const {
        data: tableData = [],
        isPending,
        isError,
        isFetching,
        refetch,
    } = useQuery<ClientDocumentRow[]>({
        queryKey: ['prospect-client-documents', prospectId, selectedVersionId],
        queryFn: async () => {
            if (!prospectId) return [];
            const entries = await getProspectClientDocuments(prospectId, selectedVersionId);
            return transformClientDocuments(entries);
        },
        enabled: !!prospectId,
        staleTime: 5 * 60_000,
    });

    const handleChangePage = (_event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleDownload = async (fileUrl: string, fileName: string) => {
        setDocumentButtonState((prev) => ({
            ...prev,
            [fileUrl]: 'loading',
        }));

        try {
            const response = await fetch(fileUrl);
            const blob = await response.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = fileName;
            a.click();
            URL.revokeObjectURL(url);
            setDocumentButtonState((prev) => ({
                ...prev,
                [fileUrl]: 'success',
            }));
        } catch (downloadError) {
            console.error('Error downloading the file:', downloadError);
            setDocumentButtonState((prev) => ({
                ...prev,
                [fileUrl]: 'default',
            }));
        }
    };

    const columns: Column<ClientDocumentRow>[] = [
        {
            id: 'documentName',
            label: 'Document',
            _render: (row) => (
                <DownloadButton
                    iconState={documentButtonState[row.downloadLink]}
                    onClick={() => handleDownload(row.downloadLink, row.documentName)}
                    label={row.documentName}
                    // Buttons center their text, which shows when a long name wraps
                    sx={{ textTransform: 'none', textAlign: 'left', justifyContent: 'flex-start' }}
                />
            ),
        },
        {
            id: 'typeOfDocument',
            label: 'Type of document',
            _render: (row) =>
                row.isSignedLegalDocument
                    ? DEFAULT_SIGNED_DOCUMENT_DISPLAY_NAME
                    : row.typeOfDocument,
        },
        {
            id: 'relatedClient',
            label: 'Related client',
            _render: (row) => (
                <Link
                    sx={{
                        fontWeight: '500',
                        cursor: 'pointer',
                        textDecoration: 'none',
                    }}
                    onClick={(e) => e.preventDefault()}
                >
                    {row.relatedClient}
                </Link>
            ),
        },
        {
            id: 'fileType',
            label: 'File type',
        },
        {
            id: 'dateOfUpload',
            label: 'Date of upload',
        },
        // Only signed retainers have actions so far
        ...(isSuperAdmin
            ? [
                  {
                      id: 'actions',
                      label: 'Actions',
                      align: 'right' as const,
                      _width: '72',
                      _render: (row) =>
                          row.isSignedLegalDocument && row.contactId ? (
                              <IconButton
                                  aria-label="Document actions"
                                  onClick={(event) =>
                                      setActionsMenu({ anchorEl: event.currentTarget, row })
                                  }
                              >
                                  <MoreHorizIcon />
                              </IconButton>
                          ) : null,
                  },
              ]
            : []),
    ];

    if (isError) {
        return (
            <ProspectDetailsErrorPage
                message="There was an error fetching client documents"
                onRetry={refetch}
                isRetrying={isFetching}
            />
        );
    }

    return (
        <Stack spacing={2} p={3}>
            {isDraftPreview && (
                <Alert severity="warning">{DRAFT_INTAKE_DOCUMENTS_DISCLAIMER}</Alert>
            )}
            <Typography variant="h3">Uploaded documents</Typography>
            <DataTable
                generateKeyFrom={'fileId'}
                isLoading={isPending}
                columns={columns}
                rows={tableData}
                customStyles={{
                    border: `1px solid ${theme.palette.neutral.borderColor}`,
                    boxShadow: 'none',
                }}
                page={page}
                rowsPerPage={rowsPerPage}
                handleChangePage={handleChangePage}
                handleChangeRowsPerPage={handleChangeRowsPerPage}
            />
            <Menu
                anchorEl={actionsMenu?.anchorEl}
                open={Boolean(actionsMenu)}
                onClose={() => setActionsMenu(null)}
            >
                <MenuItem
                    onClick={() => {
                        if (!actionsMenu) return;
                        setVoidingRow(actionsMenu.row);
                        setActionsMenu(null);
                    }}
                    sx={{ color: 'error.main' }}
                >
                    Void retainer
                </MenuItem>
            </Menu>
            {voidingRow?.contactId && prospectId && (
                <VoidRetainerDialog
                    prospectId={prospectId}
                    contactId={voidingRow.contactId}
                    clientName={voidingRow.relatedClient}
                    onClose={() => setVoidingRow(null)}
                    onVoided={() => {
                        setVoidingRow(null);
                        void refetch();
                    }}
                />
            )}
        </Stack>
    );
}
